import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function OutsourcingAccounting() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [handoffs, setHandoffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [clientFilter, setClientFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const [clients, setClients] = useState([]);
  const [clientMapping, setClientMapping] = useState([]);
  const [selected, setSelected] = useState(null);

  // Send form
  const [showSendForm, setShowSendForm] = useState(false);
  const [approvedPeriods, setApprovedPeriods] = useState([]);
  const [sendPeriodId, setSendPeriodId] = useState('');

  // Status update form
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [statusForm, setStatusForm] = useState({ handoff_status:'', accounting_reference:'', invoice_number:'', invoice_date:'', due_date:'', invoiced_amount:'', paid_amount:'', last_payment_date:'', failure_reason:'', notes:'' });

  useEffect(() => { if (companyId) { loadDashboard(); loadClients(); } }, [companyId]);
  useEffect(() => { if (companyId && tab === 'handoffs') fetchHandoffs(); }, [companyId, tab, page, clientFilter, statusFilter]);
  useEffect(() => { if (companyId && tab === 'mapping') loadMapping(); }, [companyId, tab]);

  const authFetch = async (url, opts = {}) => {
    const res = await fetch(url, { ...opts, headers: { ...getAuthHeaders(), ...(opts.headers || {}) } });
    if (res.status === 401 || res.status === 403) { navigate('/login'); return null; }
    return res;
  };

  const loadDashboard = async () => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/accounting-dashboard`);
    if (res) setDashboard(await res.json());
    setLoading(false);
  };

  const loadClients = async () => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients`);
    if (res) { const j = await res.json(); setClients(j.data || []); }
  };

  const fetchHandoffs = async () => {
    setLoading(true);
    let url = `${API}/companies/${companyId}/outsourcing/accounting?page=${page}`;
    if (clientFilter) url += `&client_id=${clientFilter}`;
    if (statusFilter) url += `&handoff_status=${statusFilter}`;
    const res = await authFetch(url);
    if (!res) return;
    const json = await res.json();
    setHandoffs(json.data || []);
    setLastPage(json.last_page || 1);
    setLoading(false);
  };

  const loadMapping = async () => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/accounting/client-mapping`);
    if (res) setClientMapping(await res.json());
  };

  const loadApprovedPeriods = async () => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/billing?billing_status=approved`);
    if (res) { const j = await res.json(); setApprovedPeriods(j.data || []); }
  };

  // ── SEND TO ACCOUNTING ──
  const sendToAccounting = async () => {
    if (!sendPeriodId) { window.alert('Select a billing period.'); return; }
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/accounting/send`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({outsourcing_billing_period_id: sendPeriodId}) });
    if (res && res.ok) { setShowSendForm(false); setSendPeriodId(''); loadDashboard(); if (tab==='handoffs') fetchHandoffs(); }
    else if (res) { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  // ── UPDATE HANDOFF STATUS ──
  const openStatusForm = (h) => {
    setSelected(h);
    setStatusForm({
      handoff_status: h.handoff_status, accounting_reference: h.accounting_reference || '',
      invoice_number: h.invoice_number || '', invoice_date: h.invoice_date || '', due_date: h.due_date || '',
      invoiced_amount: h.invoiced_amount || '', paid_amount: h.paid_amount || '',
      last_payment_date: h.last_payment_date || '', failure_reason: h.failure_reason || '', notes: h.notes || ''
    });
    setShowStatusForm(true);
  };

  const updateStatus = async () => {
    const payload = { ...statusForm };
    // Clean empty strings
    Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/accounting/${selected.id}/status`, { method:'PUT', headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload) });
    if (res && res.ok) { setShowStatusForm(false); setSelected(null); loadDashboard(); fetchHandoffs(); }
    else if (res) { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  // ── RESEND ──
  const resendHandoff = async (handoffId) => {
    if (!window.confirm('Resend this handoff to accounting?')) return;
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/accounting/${handoffId}/resend`, { method:'POST' });
    if (res && res.ok) { loadDashboard(); fetchHandoffs(); }
    else if (res) { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  // ── UPDATE CLIENT MAPPING ──
  const updateMapping = async (clientId, accountingId) => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/accounting/client-mapping/${clientId}`, { method:'PUT', headers:{'Content-Type':'application/json'}, body: JSON.stringify({accounting_customer_id: accountingId}) });
    if (res && res.ok) loadMapping();
  };

  if (!companyId) return <div style={st.page}><p>Please select a company first.</p></div>;

  return (
    <div style={st.page}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:12,marginBottom:16}}>
        <h2 style={st.title}>Accounting Handoff</h2>
        <button onClick={() => { loadApprovedPeriods(); setSendPeriodId(''); setShowSendForm(true); }} style={st.btnPrimary}>Send to Accounting</button>
      </div>

      {/* Tabs */}
      <div style={st.tabs}>
        {['dashboard','handoffs','mapping'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={tab===t ? st.tabActive : st.tab}>{t.charAt(0).toUpperCase()+t.slice(1)}</button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {tab === 'dashboard' && (
        dashboard ? (
          <div>
            <div style={st.cardsGrid}>
              <div style={st.card}><div style={st.cardLabel}>Sent</div><div style={st.cardValue}>{dashboard.sent}</div></div>
              <div style={st.card}><div style={st.cardLabel}>Invoiced</div><div style={st.cardValue}>{dashboard.invoiced}</div></div>
              <div style={st.card}><div style={st.cardLabel}>Paid</div><div style={st.cardValue}>{dashboard.paid}</div></div>
              <div style={st.card}><div style={st.cardLabel}>Failed</div><div style={{...st.cardValue,color: dashboard.failed > 0 ? '#e74c3c' : '#2c3e50'}}>{dashboard.failed}</div></div>
              <div style={st.card}><div style={st.cardLabel}>Pending Amount</div><div style={st.cardValue}>${Number(dashboard.pending_amount).toLocaleString('en-US',{minimumFractionDigits:2})}</div></div>
              <div style={st.card}><div style={st.cardLabel}>Invoiced Amount</div><div style={st.cardValue}>${Number(dashboard.invoiced_amount).toLocaleString('en-US',{minimumFractionDigits:2})}</div></div>
              <div style={st.card}><div style={st.cardLabel}>Paid Amount</div><div style={{...st.cardValue,color:'#27ae60'}}>${Number(dashboard.paid_amount).toLocaleString('en-US',{minimumFractionDigits:2})}</div></div>
              <div style={st.card}><div style={st.cardLabel}>Unmapped Clients</div><div style={{...st.cardValue,color: dashboard.unmapped_clients > 0 ? '#f39c12' : '#2c3e50'}}>{dashboard.unmapped_clients}</div></div>
            </div>
          </div>
        ) : <p>Loading dashboard...</p>
      )}

      {/* Handoffs Tab */}
      {tab === 'handoffs' && (
        <div>
          <div style={st.filterRow}>
            <select value={clientFilter} onChange={e => { setClientFilter(e.target.value); setPage(1); }} style={{...st.input,width:180}}>
              <option value="">All Clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={{...st.input,width:160}}>
              <option value="">All Statuses</option>
              {['sent','invoice_created','invoiced','paid','failed','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {initialLoading ? <p>Loading...</p> : handoffs.length === 0 ? <p style={{color:'#999'}}>No handoffs found.</p> : (
            <div style={st.tableWrap}>
              <table style={st.table}>
                <thead>
                  <tr>
                    <th style={st.th}>Client</th>
                    <th style={st.th}>Period</th>
                    <th style={st.th}>Version</th>
                    <th style={st.th}>Amount</th>
                    <th style={st.th}>Status</th>
                    <th style={st.th}>Sent At</th>
                    <th style={st.th}>Invoice #</th>
                    <th style={st.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {handoffs.map((h, i) => (
                    <tr key={h.id} style={i%2===0 ? st.rowEven : st.rowOdd}>
                      <td style={st.td}>{h.billing_period?.client?.name || '—'}</td>
                      <td style={st.td}>{h.billing_period?.billing_month}/{h.billing_period?.billing_year}</td>
                      <td style={st.td}>v{h.billing_version}</td>
                      <td style={st.td}>${Number(h.approved_total||0).toLocaleString('en-US',{minimumFractionDigits:2})}</td>
                      <td style={st.td}><span style={{...st.badge,backgroundColor:handoffClr(h.handoff_status)}}>{h.handoff_status}</span></td>
                      <td style={st.td}>{h.sent_at ? new Date(h.sent_at).toLocaleDateString() : '—'}</td>
                      <td style={st.td}>{h.invoice_number || '—'}</td>
                      <td style={st.td}>
                        <button onClick={() => openStatusForm(h)} style={st.btnSm}>Update</button>
                        {(h.handoff_status === 'failed' || h.handoff_status === 'cancelled') && (
                          <button onClick={() => resendHandoff(h.id)} style={{...st.btnSm,marginLeft:4,color:'#3498db'}}>Resend</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {lastPage > 1 && (
            <div style={st.pagination}>
              <button disabled={page<=1} onClick={() => setPage(page-1)} style={st.pageBtn}>Prev</button>
              <span>Page {page} of {lastPage}</span>
              <button disabled={page>=lastPage} onClick={() => setPage(page+1)} style={st.pageBtn}>Next</button>
            </div>
          )}
        </div>
      )}

      {/* Mapping Tab */}
      {tab === 'mapping' && (
        <div style={st.section}>
          <h3 style={{marginTop:0,marginBottom:12}}>Client → Accounting Customer Mapping</h3>
          <p style={{fontSize:13,color:'#999',marginBottom:16}}>Map outsourcing clients to their accounting system customer IDs for proper invoice creation.</p>
          {clientMapping.length === 0 ? <p style={{color:'#999'}}>No active clients.</p> : (
            <div style={st.tableWrap}>
              <table style={st.table}>
                <thead>
                  <tr>
                    <th style={st.th}>Client</th>
                    <th style={st.th}>Code</th>
                    <th style={st.th}>Accounting Customer ID</th>
                    <th style={st.th}>Mapped</th>
                    <th style={st.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clientMapping.map((c, i) => (
                    <tr key={c.id} style={i%2===0 ? st.rowEven : st.rowOdd}>
                      <td style={st.td}>{c.name}</td>
                      <td style={st.td}>{c.code}</td>
                      <td style={st.td}>
                        <input
                          value={c.accounting_customer_id || ''}
                          onChange={e => {
                            const updated = clientMapping.map(cm => cm.id === c.id ? {...cm, accounting_customer_id: e.target.value} : cm);
                            setClientMapping(updated);
                          }}
                          style={{...st.input,marginBottom:0,width:200}}
                          placeholder="Enter ID..."
                        />
                      </td>
                      <td style={st.td}>
                        {c.is_mapped ? <span style={{color:'#27ae60',fontWeight:600}}>✓ Yes</span> : <span style={{color:'#e74c3c'}}>✗ No</span>}
                      </td>
                      <td style={st.td}>
                        <button onClick={() => updateMapping(c.id, c.accounting_customer_id)} style={st.btnSm}>Save</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Send Modal */}
      {showSendForm && (
        <div style={st.overlay} onClick={() => setShowSendForm(false)}>
          <div style={st.modal} onClick={e => e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:16}}><h3 style={{margin:0}}>Send to Accounting</h3><button onClick={() => setShowSendForm(false)} style={{background:'none',border:'none',fontSize:20,cursor:'pointer'}}>✕</button></div>
            <label style={st.label}>Select Approved Billing Period *</label>
            <select value={sendPeriodId} onChange={e => setSendPeriodId(e.target.value)} style={st.input}>
              <option value="">Select...</option>
              {approvedPeriods.map(p => (
                <option key={p.id} value={p.id}>{p.client?.name || '—'} — {p.billing_month}/{p.billing_year} v{p.version} (${Number(p.total_billable||0).toLocaleString()})</option>
              ))}
            </select>
            {approvedPeriods.length === 0 && <p style={{color:'#999',fontSize:13}}>No approved billing periods available.</p>}
            <button onClick={sendToAccounting} style={{...st.btnPrimary,marginTop:8}}>Send</button>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusForm && selected && (
        <div style={st.overlay} onClick={() => setShowStatusForm(false)}>
          <div style={st.modal} onClick={e => e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:16}}><h3 style={{margin:0}}>Update Handoff Status</h3><button onClick={() => setShowStatusForm(false)} style={{background:'none',border:'none',fontSize:20,cursor:'pointer'}}>✕</button></div>
            <p style={{fontSize:13,color:'#666'}}>Handoff #{selected.id} — {selected.billing_period?.client?.name || '—'} — ${Number(selected.approved_total).toLocaleString()}</p>

            <label style={st.label}>Status *</label>
            <select value={statusForm.handoff_status} onChange={e => setStatusForm({...statusForm,handoff_status:e.target.value})} style={st.input}>
              {['sent','invoice_created','invoiced','paid','failed','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>

            <label style={st.label}>Accounting Reference</label>
            <input value={statusForm.accounting_reference} onChange={e => setStatusForm({...statusForm,accounting_reference:e.target.value})} style={st.input} />

            <div style={st.grid2}>
              <div>
                <label style={st.label}>Invoice Number</label>
                <input value={statusForm.invoice_number} onChange={e => setStatusForm({...statusForm,invoice_number:e.target.value})} style={st.input} />
              </div>
              <div>
                <label style={st.label}>Invoiced Amount</label>
                <input type="number" step="0.01" value={statusForm.invoiced_amount} onChange={e => setStatusForm({...statusForm,invoiced_amount:e.target.value})} style={st.input} />
              </div>
            </div>

            <div style={st.grid2}>
              <div>
                <label style={st.label}>Invoice Date</label>
                <input type="date" value={statusForm.invoice_date} onChange={e => setStatusForm({...statusForm,invoice_date:e.target.value})} style={st.input} />
              </div>
              <div>
                <label style={st.label}>Due Date</label>
                <input type="date" value={statusForm.due_date} onChange={e => setStatusForm({...statusForm,due_date:e.target.value})} style={st.input} />
              </div>
            </div>

            <div style={st.grid2}>
              <div>
                <label style={st.label}>Paid Amount</label>
                <input type="number" step="0.01" value={statusForm.paid_amount} onChange={e => setStatusForm({...statusForm,paid_amount:e.target.value})} style={st.input} />
              </div>
              <div>
                <label style={st.label}>Last Payment Date</label>
                <input type="date" value={statusForm.last_payment_date} onChange={e => setStatusForm({...statusForm,last_payment_date:e.target.value})} style={st.input} />
              </div>
            </div>

            {(statusForm.handoff_status === 'failed' || statusForm.handoff_status === 'cancelled') && (
              <>
                <label style={st.label}>Failure Reason</label>
                <textarea value={statusForm.failure_reason} onChange={e => setStatusForm({...statusForm,failure_reason:e.target.value})} style={{...st.input,minHeight:50}} />
              </>
            )}

            <label style={st.label}>Notes</label>
            <textarea value={statusForm.notes} onChange={e => setStatusForm({...statusForm,notes:e.target.value})} style={{...st.input,minHeight:50}} />

            <button onClick={updateStatus} style={{...st.btnPrimary,marginTop:8}}>Update</button>
          </div>
        </div>
      )}
    </div>
  );
}

function handoffClr(s) {
  const m = { sent:'#3498db', invoice_created:'#2980b9', invoiced:'#8e44ad', paid:'#27ae60', failed:'#e74c3c', cancelled:'#7f8c8d' };
  return m[s] || '#95a5a6';
}

const st = {
  page: { padding:24, maxWidth:1200, margin:'0 auto' },
  title: { fontSize:24, fontWeight:700, margin:0 },
  filterRow: { display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' },
  input: { padding:'8px 12px', borderRadius:6, border:'1px solid #ccc', fontSize:14, width:'100%', boxSizing:'border-box', marginBottom:8 },
  label: { display:'block', fontSize:13, fontWeight:600, marginBottom:4, marginTop:8 },
  tableWrap: { overflowX:'auto' },
  table: { width:'100%', borderCollapse:'collapse' },
  th: { textAlign:'left', padding:'10px 12px', borderBottom:'2px solid #3498db', fontSize:13, fontWeight:600, whiteSpace:'nowrap' },
  td: { padding:'10px 12px', borderBottom:'1px solid #eee', fontSize:14 },
  rowEven: { background:'#fafafa' },
  rowOdd: { background:'#fff' },
  badge: { display:'inline-block', padding:'3px 10px', borderRadius:12, color:'#fff', fontSize:12, fontWeight:600, textTransform:'capitalize' },
  btnPrimary: { padding:'8px 16px', background:'#3498db', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:14, fontWeight:600 },
  btnSm: { padding:'4px 10px', background:'#ecf0f1', border:'none', borderRadius:4, cursor:'pointer', fontSize:13 },
  tabs: { display:'flex', gap:0, borderBottom:'2px solid #ecf0f1', marginBottom:16 },
  tab: { padding:'10px 20px', background:'none', border:'none', cursor:'pointer', fontSize:14, color:'#999', borderBottom:'2px solid transparent' },
  tabActive: { padding:'10px 20px', background:'none', border:'none', cursor:'pointer', fontSize:14, color:'#3498db', fontWeight:600, borderBottom:'2px solid #3498db' },
  cardsGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:16, marginBottom:24 },
  card: { background:'#fff', borderRadius:10, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.08)', textAlign:'center' },
  cardLabel: { fontSize:13, color:'#888', marginBottom:6, textTransform:'uppercase', letterSpacing:0.5 },
  cardValue: { fontSize:24, fontWeight:700, color:'#2c3e50' },
  section: { background:'#fff', borderRadius:10, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.08)', marginBottom:16 },
  grid2: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 16px' },
  overlay: { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 },
  modal: { background:'#fff', borderRadius:12, padding:24, width:'90%', maxWidth:550, maxHeight:'85vh', overflowY:'auto' },
  pagination: { display:'flex', justifyContent:'center', alignItems:'center', gap:16, marginTop:16 },
  pageBtn: { padding:'6px 14px', border:'1px solid #ccc', borderRadius:6, background:'#fff', cursor:'pointer' },
};
